import styled from '@emotion/styled';

import { spacing, borderRadius, shadows, typography, colors } from '../tokens';

export type StepHeaderState = 'inactive' | 'active' | 'completed';

/**
 * Valores visuais EXATOS de TEXTOS_ABASTECIMENTO.md (Figma).
 *
 * Mantidos locais ao componente porque divergem dos tokens globais
 * (primary #000eff vs #0B29FB; text #575757 vs #3D3D3D) — trocar os tokens
 * globais seria breaking para todo o design system. Centralizados aqui para
 * não espalhar magic values pelo componente.
 */
const spec = {
  titleColorDefault: '#3D3D3D', // título do card seletor ("Local de abastecimento")
  titleColorHighlight: '#0B29FB', // título de seção ativa ("Refeitório", "Resumo")
  subtitleColor: '#3D3D3D',
  counterColor: '#3D3D3D',
  arrowBg: '#D9D9D9',
  arrowIcon: '#8C8C8C',
  arrowActiveBg: '#0B29FB',
  arrowActiveIcon: '#FFFFFF',
  fontFamily: typography.fontFamily.primary,
};

/**
 * Título "destacado" (22px azul) para seções em preenchimento; caso contrário
 * o título-seletor (16px cinza).
 */
const isHighlightTitle = (state: StepHeaderState): boolean =>
  state !== 'inactive';

/**
 * Card geral: padding 16px, fundo branco, cantos arredondados, sombra sutil.
 * SEM barra de acento lateral — a ativação é indicada apenas pela seta.
 */
export const StyledStepHeader = styled('div', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.lg};
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing.lg}; /* 16px */
  background-color: ${colors.background};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.sm};

  @media (max-width: 480px) {
    gap: ${spacing.md};
  }
`;

/**
 * Coluna esquerda: título + subtítulo opcional.
 */
export const StepHeaderContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

/**
 * Título. 16px/#3D3D3D para o seletor; 22px/#0B29FB para seção ativa.
 * Renderizado como <h3> para manter hierarquia semântica.
 */
export const StepHeaderTitle = styled('h3', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $state: StepHeaderState }>`
  margin: 0;
  padding: 0;
  font-family: ${spec.fontFamily};
  font-weight: 700;
  ${({ $state }) =>
    isHighlightTitle($state)
      ? `font-size: 22px; line-height: 28px; color: ${spec.titleColorHighlight};`
      : `font-size: 16px; line-height: 24px; color: ${spec.titleColorDefault};`}
`;

/**
 * Subtítulo: 14px/400/#3D3D3D.
 */
export const StepHeaderSubtitle = styled('p')`
  margin: 0;
  padding: 0;
  font-family: ${spec.fontFamily};
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: ${spec.subtitleColor};
`;

/**
 * Wrapper do slot direito (contador ou seta).
 */
export const StepHeaderTrailing = styled('div')`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

/**
 * Contador "Local 1 de 2": 16px/700/#3D3D3D.
 */
export const StepHeaderCounter = styled('span')`
  font-family: ${spec.fontFamily};
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${spec.counterColor};
  white-space: nowrap;
`;

/**
 * Botão-seta circular 48x48. Cinza (#D9D9D9 / ícone #8C8C8C) em repouso,
 * azul (#0B29FB / ícone branco) quando ativo.
 */
export const ArrowButton = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: ${borderRadius.full};
  cursor: pointer;
  background-color: ${({ $active }) =>
    $active ? spec.arrowActiveBg : spec.arrowBg};
  color: ${({ $active }) => ($active ? spec.arrowActiveIcon : spec.arrowIcon)};
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 3px solid ${spec.arrowActiveBg};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;
